import { useState, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  XMarkIcon,
  PencilSquareIcon,
  MinusIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  CheckIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Card,
  CardContent,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
  cn,
} from '@pantry/ui';
import {
  ITEM_UNITS,
  UNIT_DISPLAY_LABELS,
  type ItemUnit,
  getQuantityStepSize,
  getQuickSelectOptions,
  formatQuantityDisplay,
  getUnitCategory,
  convertQuantity,
} from '@pantry/api-client';
import { useStorageLocations, type StorageLocation } from '@pantry/api-client';
import { EXPIRY_PRESETS, calculateExpiryDate, parseLocalDate } from '@pantry/shared';
import type { PendingMealPrepItem } from '@pantry/shared-react/stores';
import { UnitSelector } from '../items/unit-selector';

interface PendingItemCardProps {
  item: PendingMealPrepItem;
  householdId: string;
  onUpdate: (updates: Partial<PendingMealPrepItem>) => void;
  onRemove: () => void;
}

/**
 * Validate a pending item: must have (productId || customName) && storageLocationId
 */
function isPendingItemValid(item: PendingMealPrepItem): boolean {
  const hasIdentity = !!(item.productId || item.customName);
  const hasLocation = !!item.storageLocationId;
  return hasIdentity && hasLocation;
}

/**
 * Format a date string for display (e.g., "Dec 15").
 */
function formatDate(dateString: string, locale?: string): string {
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Card component for displaying a pending meal prep item in the web app.
 * Features inline editing for all fields.
 */
export function PendingItemCard({ item, householdId, onUpdate, onRemove }: PendingItemCardProps) {
  const { t, i18n } = useLingui();
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(item.customName || item.productName || '');
  // Track display unit for unit switching (may differ from stored unit)
  const [displayUnit, setDisplayUnit] = useState<ItemUnit>(item.unit);
  // Track quantities in display unit
  const [displayQuantity, setDisplayQuantity] = useState(item.quantity);
  const [displayQuantityToUse, setDisplayQuantityToUse] = useState(
    item.quantityToUse ?? item.quantity
  );

  const { data: locations } = useStorageLocations(householdId);
  const isValid = isPendingItemValid(item);
  const selectedLocation = locations?.find((loc) => loc.id === item.storageLocationId);

  // Display name priority: customName > productName > barcode
  const displayName = item.customName || item.productName || item.barcode || t`Unknown Item`;

  // Check if unit switching is available (non-discrete units)
  const canSwitchUnits = getUnitCategory(item.unit) !== 'discrete';
  const stepSize = getQuantityStepSize(displayUnit);
  const quickOptionsHave = getQuickSelectOptions(displayUnit, 10000); // Large max for "have"
  const quickOptionsUse = getQuickSelectOptions(displayUnit, displayQuantity);

  const handleNameBlur = useCallback(() => {
    setIsEditingName(false);
    const trimmed = nameInput.trim();
    if (trimmed && trimmed !== item.productName) {
      onUpdate({ customName: trimmed });
    } else if (!trimmed && item.productName) {
      // Reset to product name if cleared
      onUpdate({ customName: undefined });
      setNameInput(item.productName);
    }
  }, [nameInput, item.productName, onUpdate]);

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameBlur();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setNameInput(item.customName || item.productName || '');
    }
  };

  // Convert display quantity to stored unit and update
  const updateStoredQuantity = useCallback(
    (newDisplayQty: number) => {
      setDisplayQuantity(newDisplayQty);
      // Convert back to stored unit
      let storedQty = newDisplayQty;
      if (displayUnit !== item.unit && canSwitchUnits) {
        const converted = convertQuantity(newDisplayQty, displayUnit, item.unit);
        if (converted !== null) {
          storedQty = Math.round(converted * 1000) / 1000;
        }
      }
      // Cap quantityToUse if needed
      const newDisplayToUse = Math.min(displayQuantityToUse, newDisplayQty);
      setDisplayQuantityToUse(newDisplayToUse);
      let storedToUse = newDisplayToUse;
      if (displayUnit !== item.unit && canSwitchUnits) {
        const converted = convertQuantity(newDisplayToUse, displayUnit, item.unit);
        if (converted !== null) {
          storedToUse = Math.round(converted * 1000) / 1000;
        }
      }
      onUpdate({ quantity: storedQty, quantityToUse: storedToUse });
    },
    [displayUnit, item.unit, canSwitchUnits, displayQuantityToUse, onUpdate]
  );

  const updateStoredQuantityToUse = useCallback(
    (newDisplayToUse: number) => {
      setDisplayQuantityToUse(newDisplayToUse);
      // Convert back to stored unit
      let storedToUse = newDisplayToUse;
      if (displayUnit !== item.unit && canSwitchUnits) {
        const converted = convertQuantity(newDisplayToUse, displayUnit, item.unit);
        if (converted !== null) {
          storedToUse = Math.round(converted * 1000) / 1000;
        }
      }
      onUpdate({ quantityToUse: storedToUse });
    },
    [displayUnit, item.unit, canSwitchUnits, onUpdate]
  );

  const handleQuantityChange = (delta: number) => {
    const newQty = Math.max(stepSize, displayQuantity + delta * stepSize);
    const rounded = Math.round(newQty * 1000) / 1000;
    updateStoredQuantity(rounded);
  };

  const handleQuantityToUseChange = (delta: number) => {
    const newToUse = Math.max(
      stepSize,
      Math.min(displayQuantity, displayQuantityToUse + delta * stepSize)
    );
    const rounded = Math.round(newToUse * 1000) / 1000;
    updateStoredQuantityToUse(rounded);
  };

  const handleDisplayUnitChange = useCallback(
    (newUnit: ItemUnit) => {
      if (newUnit === displayUnit) return;
      // Convert both quantities to new display unit
      const convertedQty = convertQuantity(displayQuantity, displayUnit, newUnit);
      const convertedToUse = convertQuantity(displayQuantityToUse, displayUnit, newUnit);
      if (convertedQty !== null) {
        setDisplayQuantity(Math.round(convertedQty * 1000) / 1000);
      }
      if (convertedToUse !== null) {
        setDisplayQuantityToUse(Math.round(convertedToUse * 1000) / 1000);
      }
      setDisplayUnit(newUnit);
    },
    [displayUnit, displayQuantity, displayQuantityToUse]
  );

  const handleBaseUnitChange = (unit: ItemUnit) => {
    // When changing the base/stored unit, reset display unit to match
    setDisplayUnit(unit);
    onUpdate({ unit });
  };

  const handleLocationChange = (locationId: string | null) => {
    onUpdate({ storageLocationId: locationId || undefined });
  };

  const handleExpiryPreset = (days: number) => {
    const targetDate = calculateExpiryDate(days);
    // Toggle off if already selected
    if (item.expiryDate === targetDate) {
      onUpdate({ expiryDate: undefined });
    } else {
      onUpdate({ expiryDate: targetDate });
    }
  };

  return (
    <Card
      className={cn(
        'border-2 border-dashed transition-all',
        isValid ? 'border-primary/50 bg-primary/5' : 'border-yellow-500 bg-yellow-50/50'
      )}
    >
      <CardContent className="py-4 space-y-3">
        {/* Header row: image, name, NEW badge, delete */}
        <div className="flex items-start gap-3">
          {/* Product image */}
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted">
            {item.productImageUrl ? (
              <img
                src={item.productImageUrl}
                alt={displayName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="text-muted-foreground text-xs">
                <Trans>No img</Trans>
              </div>
            )}
          </div>

          {/* Name and brand */}
          <div className="flex-1 min-w-0">
            {/* NEW badge */}
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/20 px-2 py-0.5 rounded-full">
                <Trans>NEW</Trans>
              </span>
            </div>

            {/* Editable name */}
            {isEditingName ? (
              <Input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                autoFocus
                className="h-7 text-sm font-medium"
                placeholder={t`Item name`}
              />
            ) : (
              <button
                onClick={() => setIsEditingName(true)}
                className="flex items-center gap-1 text-left group"
              >
                <span className="font-medium text-sm truncate">{displayName}</span>
                <PencilSquareIcon className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}

            {/* Brand */}
            {item.productBrand && (
              <p className="text-xs text-muted-foreground truncate">{item.productBrand}</p>
            )}

            {/* Barcode (if no product name) */}
            {!item.productName && item.barcode && (
              <p className="text-xs text-muted-foreground truncate">
                <Trans>Barcode: {item.barcode}</Trans>
              </p>
            )}
          </div>

          {/* Delete button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            onClick={onRemove}
            title={t`Remove item`}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Total Quantity row (what they have) */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-12">
              <Trans>Have:</Trans>
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => handleQuantityChange(-1)}
                disabled={displayQuantity <= stepSize}
              >
                <MinusIcon className="h-3 w-3" />
              </Button>
              <div className="flex items-center gap-1 min-w-[80px] justify-center">
                <span className="text-sm font-medium">
                  {formatQuantityDisplay(displayQuantity, displayUnit)}
                </span>
                {canSwitchUnits && (
                  <UnitSelector
                    value={displayUnit}
                    onChange={handleDisplayUnitChange}
                    baseUnit={item.unit}
                    className="h-6 text-xs px-1"
                  />
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => handleQuantityChange(1)}
              >
                <PlusIcon className="h-3 w-3" />
              </Button>
            </div>

            {/* Base unit selector (for discrete or changing stored unit) */}
            {!canSwitchUnits && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 gap-1">
                    {UNIT_DISPLAY_LABELS[item.unit]}
                    <ChevronDownIcon className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {ITEM_UNITS.map((unit) => (
                    <DropdownMenuItem
                      key={unit}
                      onClick={() => handleBaseUnitChange(unit)}
                      className="flex items-center justify-between"
                    >
                      <span>{UNIT_DISPLAY_LABELS[unit]}</span>
                      {item.unit === unit && <CheckIcon className="h-4 w-4 ml-2" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {/* Quick select for "have" */}
          <div className="flex gap-1 flex-wrap pl-14">
            {quickOptionsHave.slice(0, 5).map((option) => (
              <Button
                key={option.label}
                variant="outline"
                size="sm"
                className={cn(
                  'h-6 px-2 text-xs',
                  Math.abs(displayQuantity - option.value) < 0.001 && 'border-primary bg-primary/10'
                )}
                onClick={() => updateStoredQuantity(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Quantity to Use row (how much they're using now) */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-12">
              <Trans>Using:</Trans>
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => handleQuantityToUseChange(-1)}
                disabled={displayQuantityToUse <= stepSize}
              >
                <MinusIcon className="h-3 w-3" />
              </Button>
              <div className="flex items-center gap-1 min-w-[80px] justify-center">
                <span className="text-sm font-medium">
                  {formatQuantityDisplay(displayQuantityToUse, displayUnit)}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => handleQuantityToUseChange(1)}
                disabled={displayQuantityToUse >= displayQuantity}
              >
                <PlusIcon className="h-3 w-3" />
              </Button>
            </div>
            <span className="text-xs text-muted-foreground">
              of {formatQuantityDisplay(displayQuantity, displayUnit)}
            </span>
          </div>
          {/* Quick select for "using" */}
          <div className="flex gap-1 flex-wrap pl-14">
            {quickOptionsUse.map((option) => (
              <Button
                key={option.label}
                variant="outline"
                size="sm"
                className={cn(
                  'h-6 px-2 text-xs',
                  Math.abs(displayQuantityToUse - option.value) < 0.001 &&
                    'border-fresh bg-fresh/10'
                )}
                onClick={() => updateStoredQuantityToUse(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Storage location row */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-12">
            <Trans>Store:</Trans>
          </span>
          <StorageLocationDropdown
            locations={locations || []}
            selectedId={item.storageLocationId || null}
            onChange={handleLocationChange}
            hasError={!item.storageLocationId}
          />
        </div>

        {/* Expiry date row */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-muted-foreground w-12">
            <Trans>Expiry:</Trans>
          </span>
          <div className="flex gap-1 flex-wrap items-center">
            {EXPIRY_PRESETS.map(({ label, days }) => {
              const targetDate = calculateExpiryDate(days);
              const isSelected = item.expiryDate === targetDate;
              return (
                <Button
                  key={label}
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleExpiryPreset(days)}
                >
                  {label}
                </Button>
              );
            })}
            {/* Calendar popover for custom date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={
                    item.expiryDate &&
                    !EXPIRY_PRESETS.some(
                      ({ days }) => calculateExpiryDate(days) === item.expiryDate
                    )
                      ? 'default'
                      : 'outline'
                  }
                  size="sm"
                  className="h-7 px-2 gap-1"
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {item.expiryDate &&
                  !EXPIRY_PRESETS.some(
                    ({ days }) => calculateExpiryDate(days) === item.expiryDate
                  ) ? (
                    <span className="text-xs">{formatDate(item.expiryDate, i18n.locale)}</span>
                  ) : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={item.expiryDate ? parseLocalDate(item.expiryDate) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      onUpdate({ expiryDate: date.toISOString().split('T')[0] });
                    }
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Validation warning */}
        {!isValid && (
          <div className="flex items-center gap-2 text-xs text-yellow-700 bg-yellow-100 px-2 py-1.5 rounded">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
            <span>
              {!item.storageLocationId ? (
                <Trans>Select a storage location to continue</Trans>
              ) : (
                <Trans>Add a name for this item</Trans>
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Dropdown for selecting a storage location.
 */
function StorageLocationDropdown({
  locations,
  selectedId,
  onChange,
  hasError,
}: {
  locations: StorageLocation[];
  selectedId: string | null;
  onChange: (id: string | null) => void;
  hasError: boolean;
}) {
  const { t } = useLingui();
  const selectedLocation = locations.find((loc) => loc.id === selectedId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-7 gap-1 flex-1 justify-between',
            hasError && 'border-yellow-500 text-yellow-700 hover:border-yellow-600'
          )}
        >
          <span className="truncate">
            {selectedLocation?.name || t`Select location (required)`}
          </span>
          <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {locations.map((location) => (
          <DropdownMenuItem
            key={location.id}
            onClick={() => onChange(location.id)}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span>{getLocationIcon(location.type)}</span>
              <span>{location.name}</span>
            </div>
            {selectedId === location.id && <CheckIcon className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
        {locations.length === 0 && (
          <div className="px-2 py-3 text-center text-sm text-muted-foreground">
            <Trans>No storage locations</Trans>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Get emoji icon for storage location type.
 */
function getLocationIcon(type: string): string {
  switch (type) {
    case 'fridge':
      return '🧊';
    case 'freezer':
      return '❄️';
    case 'pantry':
      return '🥫';
    default:
      return '📦';
  }
}
